import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/** ISO 4217 codes are exactly three letters; anything else is a sentinel, not a currency. */
const CURRENCY_CODE_RE = /^[A-Za-z]{3}$/;

/**
 * Locale-aware currency formatter.
 * Accepts Decimal wire strings (or numbers); returns null for null/invalid input.
 */
export function useFormatCurrency(): (
  value: string | number | null,
  currency: string | null
) => string | null {
  const { i18n } = useTranslation();

  return useCallback(
    (value: string | number | null, currency: string | null): string | null => {
      if (value == null) return null;
      const amount = typeof value === 'number' ? value : Number(value);
      if (Number.isNaN(amount)) return null;
      // Backend sentinels for "no currency code" must not render as a suffix.
      if (!currency || !CURRENCY_CODE_RE.test(currency)) {
        return amount.toLocaleString(i18n.language);
      }
      try {
        return new Intl.NumberFormat(i18n.language, {
          style: 'currency',
          currency,
          maximumFractionDigits: 2
        }).format(amount);
      } catch {
        return `${amount.toLocaleString(i18n.language)} ${currency}`;
      }
    },
    [i18n.language]
  );
}
